<script setup>
import axios from 'axios'
import { Check, Loader2, Search, X } from 'lucide-vue-next'
import { computed, nextTick, ref, watch } from 'vue'
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field/index.js'
import { Button } from '@/components/ui/button/index.js'
import { Input } from '@/components/ui/input/index.js'
import { formFieldProps, useFormField } from '@/composables/fields/useFormField.js'

const selectedUser = defineModel({ type: Object, default: null })

const props = defineProps({
  ...formFieldProps,
  name: {
    type: String,
    default: null
  },
  searchEndpoint: {
    type: String,
    default: '/api/v1/users/search'
  },
  minSearchLength: {
    type: Number,
    default: 2
  },
  perPage: {
    type: Number,
    default: 10
  }
})

const emit = defineEmits(['selected', 'cleared'])

const { inputId, isError, isSuccess } = useFormField(props)

const inputRef = ref(null)
const searchTerm = ref('')
const users = ref([])
const open = ref(false)
const isLoading = ref(false)
const searchError = ref(null)
const highlightedIndex = ref(-1)
let debounceTimeout = null
let latestRequestId = 0

const selectedUserId = computed(() => selectedUser.value?.id ?? '')
const selectedUserText = computed(() => formatUser(selectedUser.value))
const trimmedSearchTerm = computed(() => searchTerm.value.trim())
const canSearch = computed(() => trimmedSearchTerm.value.length >= props.minSearchLength)

const resultMessage = computed(() => {
  if (searchError.value) {
    return 'Unable to search users.'
  }

  if (isLoading.value) {
    return 'Searching...'
  }

  if (!canSearch.value) {
    return `Type at least ${props.minSearchLength} characters.`
  }

  if (users.value.length === 0) {
    return 'No users found.'
  }

  return null
})

watch(selectedUser, (user) => {
  if (!user) {
    searchTerm.value = ''

    return
  }

  if (!open.value) {
    searchTerm.value = formatUser(user)
  }
}, { immediate: true })

watch(searchTerm, () => {
  if (!open.value || props.disabled) {
    return
  }

  scheduleSearch()
})

watch(users, () => {
  highlightedIndex.value = users.value.length > 0 ? 0 : -1
})

watch(open, (isOpen) => {
  if (isOpen) {
    scheduleSearch()
  }
})

function formatUser(user) {
  if (!user) {
    return ''
  }

  if (user.name && user.email) {
    return `${user.name} <${user.email}>`
  }

  return user.name || user.email || ''
}

function scheduleSearch() {
  window.clearTimeout(debounceTimeout)

  if (!canSearch.value) {
    users.value = []
    searchError.value = null
    isLoading.value = false

    return
  }

  debounceTimeout = window.setTimeout(() => {
    searchUsers()
  }, 250)
}

async function searchUsers() {
  const query = trimmedSearchTerm.value

  if (query.length < props.minSearchLength) {
    return
  }

  const requestId = latestRequestId + 1
  latestRequestId = requestId
  isLoading.value = true
  searchError.value = null

  try {
    const { data } = await axios.get(props.searchEndpoint, {
      params: {
        search: query,
        per_page: props.perPage,
        order_by: 'name',
        order_direction: 'asc'
      }
    })

    if (requestId === latestRequestId) {
      users.value = data.data || data
    }
  } catch (error) {
    if (requestId === latestRequestId) {
      users.value = []
      searchError.value = error
    }
  } finally {
    if (requestId === latestRequestId) {
      isLoading.value = false
    }
  }
}

function handleFocus() {
  if (props.disabled) {
    return
  }

  open.value = true
}

function handleBlur() {
  window.setTimeout(() => {
    open.value = false

    if (selectedUser.value && searchTerm.value !== selectedUserText.value) {
      searchTerm.value = selectedUserText.value
    }
  }, 120)
}

function handleKeydown(event) {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    open.value = true

    if (users.value.length === 0) {
      highlightedIndex.value = -1

      return
    }

    highlightedIndex.value = Math.min(highlightedIndex.value + 1, users.value.length - 1)
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()

    if (users.value.length === 0) {
      highlightedIndex.value = -1

      return
    }

    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
  }

  const highlightedUser = users.value[highlightedIndex.value]

  if (event.key === 'Enter' && open.value && highlightedUser) {
    event.preventDefault()
    selectUser(highlightedUser)
  }

  if (event.key === 'Escape') {
    open.value = false
  }
}

function selectUser(user) {
  selectedUser.value = user
  searchTerm.value = formatUser(user)
  users.value = []
  open.value = false
  emit('selected', user)
}

function clearSelection() {
  selectedUser.value = null
  searchTerm.value = ''
  users.value = []
  open.value = false
  emit('cleared')

  nextTick(() => {
    focusInput()
  })
}

function focusInput() {
  inputRef.value?.$el?.focus()
}

defineExpose({
  focusInput
})
</script>

<template>
  <Field class="gap-1">
    <FieldLabel v-if="props.label" :for="inputId">
      {{ props.label }}
    </FieldLabel>

    <div class="relative">
      <Search class="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        :id="inputId"
        ref="inputRef"
        v-model:model-value="searchTerm"
        autocomplete="off"
        :disabled="props.disabled"
        :is-error="isError"
        :is-success="isSuccess"
        :placeholder="props.placeholder || 'Search users...'"
        class="pl-8 pr-16"
        role="combobox"
        :aria-expanded="open"
        :aria-controls="`${inputId}-results`"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
      />
      <div class="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-1">
        <Loader2 v-if="isLoading" class="size-4 animate-spin text-muted-foreground" />
        <Button v-if="selectedUser && !props.disabled" type="button" variant="ghost" size="icon-sm" class="size-6" aria-label="Clear selected user" @mousedown.prevent @click="clearSelection">
          <X class="size-3.5" />
        </Button>
      </div>

      <div v-if="open" :id="`${inputId}-results`" class="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md" role="listbox">
        <div v-if="resultMessage" class="px-2 py-2 text-sm text-muted-foreground">
          {{ resultMessage }}
        </div>

        <button v-for="(user, index) in users" :key="user.id" type="button" class="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground" :class="{ 'bg-accent text-accent-foreground': index === highlightedIndex }" role="option" :aria-selected="selectedUser?.id === user.id" @mousedown.prevent @mouseenter="highlightedIndex = index" @click="selectUser(user)">
          <span class="min-w-0 flex-1">
            <span class="block truncate font-medium">{{ user.name }}</span>
            <span class="block truncate text-xs text-muted-foreground">{{ user.email }}</span>
          </span>
          <Check v-if="selectedUser?.id === user.id" class="size-4 shrink-0" />
        </button>
      </div>
    </div>

    <input v-if="props.name" type="hidden" :name="props.name" :value="selectedUserId">

    <FieldDescription v-if="isError && props.errorMessage" class="font-medium text-destructive">
      {{ props.errorMessage }}
    </FieldDescription>
    <FieldDescription v-if="props.description">
      {{ props.description }}
    </FieldDescription>
  </Field>
</template>
